import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Snowflake, RotateCcw, Loader2, CheckSquare, Square } from 'lucide-react';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Spring checklist: archived seasonal-hold / on-hold jobs with one-tap bulk restore.
export const SeasonalRevivePanel = ({ token, onRestored, toast }) => {
  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/job-housekeeping/seasonal-hold`, { headers: { Authorization: `Bearer ${token}` } });
      setJobs(data.jobs || []);
      setSelected(new Set());
    } catch { setJobs([]); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  if (jobs.length === 0) return null;
  const allSelected = selected.size === jobs.length;
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(jobs.map((j) => j.id)));

  const revive = async (ids) => {
    if (!ids.length) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/job-housekeeping/revive-bulk`, { job_ids: ids }, { headers: { Authorization: `Bearer ${token}` } });
      toast?.({ title: `Restored ${data.restored.length} job${data.restored.length === 1 ? '' : 's'}`, description: 'They are back on the To-Do / Paused tabs — resume when the crew is ready.' });
      await load();
      onRestored?.();
    } catch (e) {
      toast?.({ title: 'Could not restore', description: e?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl mb-4 overflow-hidden" style={{ background: '#1A2433', border: '1px solid rgba(30,136,229,0.45)' }} data-testid="seasonal-revive-panel">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <Snowflake className="w-4 h-4 text-[#1E88E5]" />
        <button onClick={() => setOpen((o) => !o)} className="text-sm font-semibold text-white text-left" data-testid="seasonal-revive-toggle">
          Seasonal revive list · {jobs.length} job{jobs.length === 1 ? '' : 's'} waiting for next season
        </button>
        <span className="text-[11px] text-slate-500">archived as seasonal hold / on hold</span>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={toggleAll} className="border-[#2D3748] text-slate-300 h-8" data-testid="seasonal-select-all">
            {allSelected ? <CheckSquare className="w-4 h-4 mr-1" /> : <Square className="w-4 h-4 mr-1" />}{allSelected ? 'Clear' : 'Select all'}
          </Button>
          <Button size="sm" onClick={() => revive([...selected])} disabled={busy || selected.size === 0} className="bg-[#1E88E5] hover:bg-blue-600 text-white h-8" data-testid="seasonal-restore-selected">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />} Restore selected ({selected.size})
          </Button>
        </div>
      </div>
      {open && (
        <ul className="border-t border-[#2D3748] divide-y divide-[#2D3748]">
          {jobs.map((j) => (
            <li key={j.id} className="flex flex-wrap items-center gap-3 px-4 py-2 text-sm" data-testid={`seasonal-row-${j.id}`}>
              <input type="checkbox" checked={selected.has(j.id)} onChange={() => toggle(j.id)} className="w-4 h-4" data-testid={`seasonal-check-${j.id}`} />
              <span className="text-white">{j.job_number ? <span className="font-mono text-xs text-[#1E88E5] mr-1">{j.job_number}</span> : null}{j.title}</span>
              {j.customer_name && <span className="text-xs text-slate-400">{j.customer_name}</span>}
              <span className="text-[11px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,160,0,0.15)', color: '#FFA000' }}>{String(j.status || '').replace('_', ' ')}</span>
              <span className="text-[11px] text-slate-500">{String(j.archive_reason || '').replace('_', ' ')} · {j.days_archived} days · {Number(j.total_hours_worked || 0).toFixed(1)} h worked</span>
              <Button size="sm" variant="outline" onClick={() => revive([j.id])} disabled={busy} className="ml-auto border-[#2D3748] text-slate-200 h-7 text-xs" data-testid={`seasonal-restore-${j.id}`}>
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restore
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SeasonalRevivePanel;
